import { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mail, RefreshCw } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';

const CODE_LENGTH = 6;

export function ConfirmEmailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { email?: string; inviteToken?: string } | null;
  const email = state?.email;
  const inviteToken = state?.inviteToken;

  const verifyEmailOtp = useAuthStore((s) => s.verifyEmailOtp);

  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = useCallback((index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }, []);

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !digits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [digits],
  );

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    const next = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    inputRefs.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) {
      setError('Email address not found. Please go back and sign up again.');
      return;
    }
    const code = digits.join('');
    if (code.length < CODE_LENGTH) {
      setError('Please enter all 6 digits.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await verifyEmailOtp(email, code);
      if (inviteToken) {
        navigate(`/invite?token=${inviteToken}`);
      } else {
        navigate('/login', { state: { email } });
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Invalid or expired code. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (!email) return;
    setResending(true);
    setResent(false);
    setError(null);
    try {
      const resp = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error((body as { error?: { message?: string } }).error?.message ?? 'Failed to resend code');
      }
      setResent(true);
      setDigits(Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  }

  const codeComplete = digits.join('').length === CODE_LENGTH;

  return (
    <>
      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-brand/10 border border-brand/25 mx-auto mb-4">
        <Mail className="w-6 h-6 text-brand" />
      </div>

      <h2 className="text-xl font-semibold text-gray-100 mb-2 text-center">Verify your email</h2>

      <p className="text-sm text-gray-500 text-center mb-6">
        {email ? (
          <>
            We sent a 6-digit code to{' '}
            <span className="text-gray-300 font-medium">{email}</span>.
          </>
        ) : (
          'We sent a 6-digit code to your email address.'
        )}{' '}
        Enter it below to activate your account.
      </p>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-loss/10 border border-loss/25 text-sm text-loss text-center">
          {error}
        </div>
      )}

      {resent && !error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-gain/10 border border-gain/25 text-sm text-gain text-center">
          A new code has been sent to your email.
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="flex gap-2 justify-center mb-6" onPaste={handlePaste}>
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => {
                inputRefs.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className="w-11 h-14 text-center text-xl font-semibold bg-surface-2 border border-surface-4 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand caret-transparent"
              autoFocus={i === 0}
            />
          ))}
        </div>

        <button
          type="submit"
          disabled={loading || !codeComplete || !email}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Verifying…' : 'Verify email'}
        </button>
      </form>

      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={handleResend}
          disabled={resending || !email}
          className="text-sm text-gray-500 hover:text-gray-300 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${resending ? 'animate-spin' : ''}`} />
          {resending ? 'Sending…' : "Didn't receive it? Resend code"}
        </button>
      </div>
    </>
  );
}
